/**
 * What the Chats page's Calendar Meetings tab says when it has no rows — the next step that would put
 * a conversation there, or nothing when there is none to take.
 *
 * `null` for either fact means the read failed or has not answered. An unknown fact never produces a
 * CTA: sending someone to connect a calendar they already have, or to switch on a Meetings connection
 * the server would refuse (a site outside the release answers 501), is worse than the plain line.
 *
 * Import-free on purpose, so a node-env suite can pin it (orion-web CLAUDE.md, Tests).
 */
export type MeetingsChatsEmpty = 'loading' | 'connect-calendar' | 'connect-meetings' | 'empty';

export interface MeetingsSetup {
  calendarConnected: boolean | null;
  meetingsConnected: boolean | null;
}

/**
 * The cache key of that answer. It lives here, import-free, so the calendar connect and disconnect
 * paths can invalidate it without pulling in the meetings data layer. Every calendar connect happens
 * in-page (a popup, Google's own consent window, the iCloud form), and one of them — the docked chat's
 * suggested-task chip — can run beside this very tab, so no remount or reload refreshes it for them.
 */
export const MEETINGS_SETUP_KEY = ['meetings', 'setup'] as const;

export function meetingsChatsEmpty(setup: MeetingsSetup | undefined): MeetingsChatsEmpty {
  if (!setup) return 'loading';
  if (setup.calendarConnected === false) return 'connect-calendar';
  if (setup.calendarConnected === true && setup.meetingsConnected === false) return 'connect-meetings';
  return 'empty';
}

/** Whether a `/api/meetings/connection` answer means Meetings is on: the connection row alone is the consent. */
export function meetingsConnectionPresent(json: Record<string, unknown>): boolean {
  return typeof json.connection === 'object' && json.connection !== null;
}

/**
 * Whether a `/api/calendars` answer holds a calendar Maestro can join meetings from. Only Google counts:
 * she scans Google calendars alone, so an Outlook- or iCloud-only account is still asked to connect one.
 */
export function hasMeetingsCalendar(json: Record<string, unknown>): boolean {
  const calendars = Array.isArray(json.calendars) ? (json.calendars as { provider?: unknown }[]) : [];
  return calendars.some((calendar) => calendar?.provider === 'GOOGLE');
}
